import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Sidebar, { clearSession } from "../components/Sidebar";
import {
  Dashboard6Styles, ModalShell, PagePreloader, Avatar, StatusChip, CardHead, PageHeader, KpiStrip, Pagination, ColumnPicker, useBootstrapModal,
} from "../components/ModalUI";
import { RequestDetailModal, ManageCategoriesModal } from "../components/AdminMaintenanceModals";
import * as XLSX from "xlsx";
import { normalizeMaintenanceRequest } from "../utils/enums";

const DEV_PROXY_BASE = "/api";
const PROD_API_BASE = "https://zangon-web-api.azurewebsites.net";
const API_BASE = process.env.NODE_ENV === "development" ? DEV_PROXY_BASE : PROD_API_BASE;
const SESSION_KEY = "zangon_session";

const PAGE_SIZE = 30; 

const HOUSE_TYPE_IMAGES = {
  "1 Bedroom Bungalow": "/assets/media/zangon/1bedroombungalo.jpg",
  "1 Bedrooms Bungalow": "/assets/media/zangon/1bedroombungalo.jpg",
  "2 Bedroom Bungalow": "/assets/media/zangon/2bedroom.jpg",
  "2 Bedrooms Bungalow": "/assets/media/zangon/2bedroom.jpg",
  "3 Bedroom Bungalow": "/assets/media/zangon/3bedroomsbungalo.jpg",
  "3 Bedrooms Bungalow": "/assets/media/zangon/3bedroomsbungalo.jpg",
  "4 Bedroom Bungalow": "/assets/media/zangon/4bedroombungalo.jpg",
  "4 Bedrooms Bungalow": "/assets/media/zangon/4bedroombungalo.jpg",
  "5 Bedroom Bungalow": "/assets/media/zangon/5-light.jpg",
  "5 Bedrooms Bungalow": "/assets/media/zangon/5-light.jpg",
  "6 Bedroom Bungalow": "/assets/media/zangon/6bedroombungalow.jpg",
  "6 Bedrooms Bungalow": "/assets/media/zangon/6bedroombungalow.jpg",
  "6 Bedroom Terrace Duplex": "/assets/media/zangon/6bedroomterraceduple.jpg",
  "6 Bedrooms Terrace Duplex": "/assets/media/zangon/6bedroomterraceduple.jpg",
};

function getHouseImage(typeName) {
  if (!typeName) return "/assets/media/zangon/3bedroomsbungalo.jpg";
  const norm = typeName.toLowerCase().replace(/[^a-z0-9]/g, "").replace(/bedrooms?/g, "bedroom");
  if (norm.includes("1bedroom") || norm.includes("1bd")) return "/assets/media/zangon/1bedroombungalo.jpg";
  if (norm.includes("2bedroom") || norm.includes("2bd")) return "/assets/media/zangon/2bedroom.jpg";
  if (norm.includes("3bedroombungalow") || norm.includes("3bedroombungalo") || norm.includes("3bd")) return "/assets/media/zangon/3bedroomsbungalo.jpg";
  if (norm.includes("4bedroombungalow") || norm.includes("4bedroombungalo") || norm.includes("4bd")) return "/assets/media/zangon/4bedroombungalo.jpg";
  if (norm.includes("5bedroom")) return "/assets/media/zangon/5-light.jpg";
  if (norm.includes("6bedroombungalow")) return "/assets/media/zangon/6bedroombungalow.jpg";
  if (norm.includes("6bedroomterraceduplex") || norm.includes("6bedroomterraceduple") || (norm.includes("6bedroom") && norm.includes("terrace"))) return "/assets/media/zangon/6bedroomterraceduple.jpg";
  return HOUSE_TYPE_IMAGES[typeName] || "/assets/media/zangon/3bedroomsbungalo.jpg";
}


const PRIORITY_META = {
  Low: { badge: "badge-light-info text-info", dot: "#3b82f6", color: "text-info" },
  Normal: { badge: "badge-light-secondary text-gray-700", dot: "#94a3b8", color: "text-gray-700" },
  High: { badge: "badge-light-warning text-warning", dot: "#f59e0b", color: "text-warning" },
  Emergency: { badge: "badge-light-danger text-danger", dot: "#ef4444", color: "text-danger" },
};
function safeJsonParse(text) {
  try { return JSON.parse(text); } catch { return null; }
}

function getSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; }
}

function setSession(session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function getAccessToken() { return getSession()?.accessToken || ""; }
export function getRefreshToken() { return getSession()?.refreshToken || ""; }

async function fetchWithTimeout(url, options = {}, timeoutMs = 60000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (err) {
    if (err?.name === "AbortError") throw new Error("Request timed out. Try again.");
    throw err;
  } finally {
    clearTimeout(id);
  }
}

async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error("No refresh token.");
  const res = await fetchWithTimeout(`${API_BASE}/auth/refresh`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || "Token refresh failed.");
  const next = { ...(getSession() || {}), ...(json?.data ?? json ?? {}) };
  setSession(next);
  localStorage.setItem("auth_token", next?.accessToken || "");
  return next;
}

async function authFetch(url, options = {}) {
  let token = getAccessToken();
  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
    ...(options.headers || {}),
  };
  if (options.body && typeof options.body === "string" && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  let res = await fetchWithTimeout(url, { ...options, headers });
  if (res.status === 401) {
    try {
      const renewed = await refreshAccessToken();
      token = renewed.accessToken;
      headers.Authorization = `Bearer ${token}`;
      res = await fetchWithTimeout(url, { ...options, headers });
    } catch {
      clearSession();
      window.location.href = "/sign-in";
      throw new Error("Session expired. Please sign in again.");
    }
  }
  return res;
}

function parseArrayData(json) {
  if (!json) return [];
  const raw = json.data;
  if (raw && typeof raw === "object" && Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const parsed = safeJsonParse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && Array.isArray(parsed.data)) return parsed.data;
  }
  if (Array.isArray(json)) return json;
  return [];
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

const RequestListSkeleton = () => (
  <div className="d6-card d-flex align-items-center position-relative mb-2 py-4 px-5 placeholder-glow overflow-hidden" style={{ minHeight: 90 }}>
    <div className="position-absolute top-0 start-0 h-100 bg-secondary opacity-25 w-4px"></div>
    <div className="placeholder rounded-circle me-4 flex-shrink-0" style={{ width: 48, height: 48 }}></div>
    <div className="d-flex flex-column flex-grow-1 gap-2">
      <span className="placeholder rounded w-50 h-15px"></span>
      <span className="placeholder rounded w-25 h-12px"></span>
    </div>
    <span className="placeholder rounded-pill ms-auto w-75px h-20px"></span>
  </div>
);

function DownloadRequestsModal({ modalRef, estateId, filters }) {
  const [options, setOptions] = useState({
    title: true,
    tenantName: true,
    phase: true,
    houseNumber: true,
    category: true,
    priority: true,
    status: true,
    createdDate: true,
  });
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    const activeHeaders = Object.keys(options).filter(k => options[k]);
    if (activeHeaders.length === 0) {
      toast.error("Please select at least one column to download.");
      return;
    }

    setDownloading(true);
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/maintenance-requests?pageIndex=0&pageSize=30`);
      if (!res.ok) throw new Error("Failed to fetch maintenance requests");
      const json = safeJsonParse(await res.text());
      
      let items = [];
      if (json?.data?.data) items = json.data.data;
      else if (json?.data) items = Array.isArray(json.data) ? json.data : [];
      else if (Array.isArray(json)) items = json;
      items = items.map(normalizeMaintenanceRequest);

      const enriched = items.map(req => {
        let fullBuildingName = req.buildingNumber || "Unassigned";
        let phase = "—";
        let house = req.buildingNumber || "Unassigned";

        if (req.buildingNumber && req.requesterCurrentBuildings) {
          const mb = req.requesterCurrentBuildings.find(b => b.number === req.buildingNumber);
          if (mb?.phaseName) {
            fullBuildingName = `${mb.phaseName} — ${req.buildingNumber}`;
            phase = mb.phaseName;
            house = req.buildingNumber;
          }
        }

        const titleStr = req.requesterTitle ? `${req.requesterTitle} ` : "";
        const formattedTenantName = `${titleStr}${req.requesterName || "Unknown"}`.trim();

        return {
          id: req.id,
          tenantId: req.requesterId,
          tenantName: formattedTenantName,
          buildingNumber: fullBuildingName,
          phaseName: phase,
          houseNumber: house,
          title: req.title || req.shortDescription || "Maintenance Request",
          description: req.shortDescription || "",
          priority: req.priorityLevel || "Normal",
          category: req.category || "General",
          status: req.status || "Created",
          createdDate: req.createdDate ? new Date(req.createdDate).toLocaleDateString() : '—',
          rawCreatedDate: req.createdDate
        };
      });

      const filtered = enriched.filter(r => {
        const q = (filters.search || "").trim().toLowerCase();
        const matchSearch = !q || 
          r.title.toLowerCase().includes(q) || 
          r.tenantName.toLowerCase().includes(q) || 
          r.category.toLowerCase().includes(q) || 
          (r.buildingNumber && r.buildingNumber.toLowerCase().includes(q));
        const matchStatus = filters.statusFilter === "all" || r.status === filters.statusFilter;
        const matchPriority = filters.priorityFilter === "all" || r.priority === filters.priorityFilter;
        const matchPhase = filters.phaseFilter === "all" || r.phaseName === filters.phaseFilter;
        const matchCategory = filters.categoryFilter === "all" || r.category === filters.categoryFilter;
        return matchSearch && matchStatus && matchPriority && matchPhase && matchCategory;
      });

      if (filtered.length === 0) {
        toast.error("No records to export matching active filters.");
        setDownloading(false);
        return;
      }

      const headerMapping = {
        title: "Title / Description",
        tenantName: "Tenant Name",
        phase: "Phase",
        houseNumber: "House Number",
        category: "Category",
        priority: "Priority",
        status: "Status",
        createdDate: "Created Date"
      };

      const formattedData = filtered.map(item => {
        const row = {};
        activeHeaders.forEach(key => {
          const columnName = headerMapping[key];
          switch(key) {
            case 'title': row[columnName] = item.title || ''; break;
            case 'tenantName': row[columnName] = item.tenantName || ''; break;
            case 'phase': row[columnName] = item.phaseName || ''; break;
            case 'houseNumber': row[columnName] = item.houseNumber || ''; break;
            case 'category': row[columnName] = item.category || ''; break;
            case 'priority': row[columnName] = item.priority || ''; break;
            case 'status': row[columnName] = item.status || ''; break;
            case 'createdDate': row[columnName] = item.createdDate || ''; break;
            default: row[columnName] = '';
          }
        });
        return row;
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(formattedData);
      XLSX.utils.book_append_sheet(wb, ws, "Service Requests");
      XLSX.writeFile(wb, `maintenance_requests_log_${new Date().toISOString().split("T")[0]}.xlsx`);

      toast.success("Download started successfully!");
      if (window.bootstrap?.Modal && modalRef.current) {
        window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
      }
    } catch (err) {
      toast.error("Failed to generate download.");
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  const closeModal = () => {
    if (window.bootstrap?.Modal && modalRef.current) {
      window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
    }
  };

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-file-down"
      title="Download Request Log"
      onClose={closeModal}
      closeDisabled={downloading}
      width={640}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={closeModal} disabled={downloading}>Cancel</button>
          <button type="button" className="btn btn-success d6-mbtn" onClick={handleDownload} disabled={downloading}>
            {downloading ? (
              <><span className="spinner-border spinner-border-sm me-2"></span>Exporting…</>
            ) : (
              <><i className="ki-outline ki-file-down fs-5 me-2.5 text-white"></i>Download Export</>
            )}
          </button>
        </>
      }
    >
      <ColumnPicker options={options} setOptions={setOptions} disabled={downloading} note="Exports the requests that match your current filters." />
    </ModalShell>
  );
}

export default function TenantRequests() {
  const navigate = useNavigate();
  const session = getSession();
  const estateId = session?.estateId || "";

  // Pagination & List states
  const [pageReady, setPageReady] = useState(false);
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [phaseFilter, setPhaseFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const [staticStats, setStaticStats] = useState({ total: 0, open: 0, inProgress: 0, resolved: 0 });

  const detailModalRef = useRef(null);
  const downloadModalRef = useRef(null);
  const categoriesModalRef = useRef(null);
  const categoriesModal = useBootstrapModal(categoriesModalRef);

  const fetchRequests = useCallback(async () => {
    if (!estateId) { setIsLoading(false); setPageReady(true); return; }
    setIsLoading(true);
    try {
      const searchQ = search ? `&search=${encodeURIComponent(search.trim())}` : "";
      const res = await authFetch(`${API_BASE}/estates/${estateId}/maintenance-requests?pageIndex=${page - 1}&pageSize=${PAGE_SIZE}${searchQ}`);
      const json = res.ok ? safeJsonParse(await res.text()) : null;
      
      if (json?.data?.totalCount !== undefined) {
        setTotalCount(json.data.totalCount);
      } else if (json?.totalCount !== undefined) {
        setTotalCount(json.totalCount);
      }

      const items = parseArrayData(json).map(normalizeMaintenanceRequest);

      const allRequests = items.map(req => {
        let fullBuildingName = req.buildingNumber || "Unassigned";
        let phase = "—";
        let house = req.buildingNumber || "Unassigned";

        if (req.buildingNumber && req.requesterCurrentBuildings) {
          const mb = req.requesterCurrentBuildings.find(b => b.number === req.buildingNumber);
          if (mb?.phaseName) {
            fullBuildingName = `${mb.phaseName} - ${req.buildingNumber}`;
            phase = mb.phaseName;
            house = req.buildingNumber;
          }
        }

        // --- NEW: Format the title and name ---
        const titleStr = req.requesterTitle ? `${req.requesterTitle} ` : "";
        const formattedTenantName = `${titleStr}${req.requesterName || "Unknown"}`.trim();

        return {
          id: req.id,
          tenantId: req.requesterId,
          tenantName: formattedTenantName, // <-- Updated here
          requesterTitle: req.requesterTitle || "", // <-- Save it just in case
          tenantProfilePictureUrl: req.requesterImageUrl || null,
          buildingId: req.buildingId,
          buildingNumber: fullBuildingName,
          phaseName: phase,
          houseNumber: house,
          title: req.title || req.shortDescription || "Maintenance Request",
          description: req.shortDescription || "",
          priority: req.priorityLevel || "Normal",
          category: req.category || "General",
          status: req.status || "Created",
          uploadedFileUrls: req.uploadedFileUrls || [],
          createdDate: req.createdDate,
          lastModified: req.lastModified,
        };
      });

      const combinedRequests = [...allRequests];

      combinedRequests.sort((a, b) => {
        const order = { Created: 0, "In Progress": 1, Completed: 2, Canceled: 3 };
        const diff = (order[a.status] ?? 1) - (order[b.status] ?? 1);
        return diff !== 0 ? diff : new Date(b.createdDate || 0) - new Date(a.createdDate || 0);
      });

      setRequests(combinedRequests);

      // Lock stats on initial load so they don't break when paginating or filtering locally
      if (!search && page === 1 && statusFilter === "all" && priorityFilter === "all" && phaseFilter === "all" && categoryFilter === "all") {
        setStaticStats({
          total: json?.data?.totalCount || json?.totalCount || allRequests.length,
          open: combinedRequests.filter(r => r.status === "Created").length,
          inProgress: combinedRequests.filter(r => r.status === "In Progress").length,
          resolved: combinedRequests.filter(r => r.status === "Completed" || r.status === "Canceled").length,
        });
      }

    } catch (err) {
      toast.error("Failed to load maintenance requests.");
      console.error(err);
    } finally {
      setIsLoading(false);
      setPageReady(true);
    }
  }, [estateId, page, search]); // Added page and search to dependency array

  const fetchedState = useRef({ estateId: null, page: null, search: null });
  useEffect(() => {
    const s = fetchedState.current;
    if (s.estateId === estateId && s.page === page && s.search === search) return;
    s.estateId = estateId; s.page = page; s.search = search;
    fetchRequests();
  }, [fetchRequests, estateId, page, search]);

  // Reset to page 1 whenever any filter is altered
  useEffect(() => { 
    setPage(1); 
  }, [search, statusFilter, priorityFilter, phaseFilter, categoryFilter]);

  useEffect(() => {
    const body = document.body;
    body.id = "kt_app_body";
    body.classList.add("app-default");
    body.setAttribute("data-kt-app-header-fixed-mobile", "true");
    body.setAttribute("data-kt-app-toolbar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-fixed", "true");
    body.setAttribute("data-kt-app-sidebar-push-header", "true");
    body.setAttribute("data-kt-app-sidebar-push-toolbar", "true");
    body.setAttribute("data-kt-app-sidebar-push-footer", "true");

    const html = document.documentElement;
    const saved = localStorage.getItem("kt_theme_mode") || "light";
    html.setAttribute("data-bs-theme", saved);

    const onClick = (e) => {
      const el = e.target.closest('[data-kt-element="mode"]');
      if (!el) return;
      const mode = el.getAttribute("data-kt-value");
      if (mode) {
        if (window.KTThemeMode?.setMode) window.KTThemeMode.setMode(mode);
        else { html.setAttribute("data-bs-theme", mode); localStorage.setItem("kt_theme_mode", mode); }
      }
    };
    document.addEventListener("click", onClick);

    const init = () => {
      window.KTThemeMode?.init?.();
      window.KTComponents?.init?.();
      window.KTMenu?.createInstances?.();
      window.KTDrawer?.createInstances?.();
      window.KTScroll?.createInstances?.();
      window.KTScrollTop?.createInstances?.();
    };
    init();
    const t = setTimeout(init, 0);
    return () => { document.removeEventListener("click", onClick); clearTimeout(t); };
  }, []);

  const handleStatusChange = (requestId, newStatus) => {
    setRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: newStatus } : r));
  };

  const handleOpenRequest = (req) => {
    setSelectedRequest(req);
    if (window.bootstrap?.Modal && detailModalRef.current) {
      window.bootstrap.Modal.getOrCreateInstance(
        detailModalRef.current, { backdrop: "static" },
      ).show();
    }
  };

  const { phaseOptions, categoryOptions } = useMemo(() => ({
    phaseOptions: Array.from(new Set(requests.map(r => r.phaseName).filter(p => p !== "—" && p))).sort(),
    categoryOptions: Array.from(new Set(requests.map(r => r.category).filter(Boolean))).sort(),
  }), [requests]);

  const filtered = requests.filter(r => {
    const q = search.trim().toLowerCase();
    const matchSearch = !q || r.title.toLowerCase().includes(q) || r.tenantName.toLowerCase().includes(q) || r.category.toLowerCase().includes(q) || (r.buildingNumber && r.buildingNumber.toLowerCase().includes(q));
    const matchStatus = statusFilter === "all" || r.status === statusFilter;
    const matchPriority = priorityFilter === "all" || r.priority === priorityFilter;
    const matchPhase = phaseFilter === "all" || r.phaseName === phaseFilter;
    const matchCategory = categoryFilter === "all" || r.category === categoryFilter;
    return matchSearch && matchStatus && matchPriority && matchPhase && matchCategory;
  });

  const totalPages = Math.max(1, Math.ceil((totalCount || 1) / PAGE_SIZE));

  return (
    <>
      <Dashboard6Styles />

      <PagePreloader visible={!pageReady} />

      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">
          <div
            id="kt_app_header"
            className="app-header"
            data-kt-sticky="true"
            data-kt-sticky-activate="{default: false, lg: true}"
            data-kt-sticky-name="app-header-sticky"
            data-kt-sticky-offset="{default: false, lg: '300px'}"
          >
            <div
              className="app-container container-fluid d-flex flex-stack align-items-center my-6"
              id="kt_app_header_container"
            >
              <div className="d-flex align-items-center d-block d-lg-none ms-n3">
                <div
                  className="btn btn-icon btn-active-color-primary w-40px h-40px me-2"
                  id="kt_app_sidebar_mobile_toggle"
                >
                  <i className="ki-outline ki-abstract-14 fs-2"></i>
                </div>
                <Link to="/dashboard">
                  <img alt="Logo" src="/assets/media/zangon-fav.png" className="h-30px" />
                </Link>
              </div>

              <PageHeader breadcrumb="Tenant Requests" title="Maintenance Requests">
                <button type="button" className="btn btn-light d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold" onClick={() => categoriesModal.show()}>
                  <i className="ki-outline ki-setting-2 fs-6 me-sm-2"></i>
                  <span className="d-none d-sm-inline">Categories</span>
                </button>
                <button
                  type="button"
                  className="btn btn-success d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold"
                  onClick={() => {
                    if (window.bootstrap?.Modal && downloadModalRef.current) {
                      window.bootstrap.Modal.getOrCreateInstance(downloadModalRef.current, { backdrop: "static" }).show();
                    }
                  }}
                >
                  <i className="ki-outline ki-file-down fs-6 me-sm-2 text-white"></i>
                  <span className="d-none d-sm-inline">Download Log</span>
                </button>
              </PageHeader>
            </div>
          </div>

          <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">
            <Sidebar collapsible />
            <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
              <div className="d-flex flex-column flex-column-fluid">
                <div id="kt_app_content" className="app-content flex-column-fluid">
                  <div id="kt_app_content_container" className="app-container container-fluid">

                    <KpiStrip
                      loading={isLoading}
                      items={[
                        { icon: "ki-wrench", label: "Total Requests", value: staticStats.total, sub: "Maintenance records" },
                        { icon: "ki-information-2", label: "Open Requests", value: staticStats.open, sub: "Pending resolution" },
                        { icon: "ki-time", label: "In Progress", value: staticStats.inProgress, sub: "Being serviced" },
                        { icon: "ki-check-circle", label: "Resolved Requests", value: staticStats.resolved, sub: "Completed tasks" },
                      ]}
                    />

                    <div className="d6-card p-6 mb-5">
                      <CardHead title="Service Requests" subtitle="Filter & track maintenance requests." className="mb-5">
                        <div className="position-relative">
                          <i className="ki-outline ki-magnifier fs-5 position-absolute top-50 start-0 translate-middle-y ms-3 text-gray-400"></i>
                          <input
                            className="d6m-input ps-10"
                            style={{ width: 280, minHeight: 40, height: 40, borderRadius: 999 }}
                            placeholder="Tenant, building, phase, category…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                          />
                        </div>
                        <select className="d6m-input" style={{ width: 150, minHeight: 40, height: 40, borderRadius: 999 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                          <option value="all">All Statuses</option>
                          <option value="Created,In Progress">In Progress</option>
                          <option value="Completed">Completed</option>
                          <option value="Canceled">Canceled</option>
                        </select>
                        <select className="d6m-input" style={{ width: 140, minHeight: 40, height: 40, borderRadius: 999 }} value={phaseFilter} onChange={e => setPhaseFilter(e.target.value)}>
                          <option value="all">All Phases</option>
                          {phaseOptions.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                        <select className="d6m-input" style={{ width: 160, minHeight: 40, height: 40, borderRadius: 999 }} value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                          <option value="all">All Categories</option>
                          {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </CardHead>

                      <div>
                        {isLoading ? (
                          [1, 2, 3, 4, 5].map((n) => <RequestListSkeleton key={n} />)
                        ) : filtered.length === 0 ? (
                          <div className="d6m-empty py-12">
                            <i className="ki-outline ki-wrench fs-3x text-gray-300 d-block mb-3"></i>
                            <div className="d6-title text-gray-900 mb-1">
                              {requests.length === 0 ? "No requests found." : "No results match your filters."}
                            </div>
                            <div className="fs-8 text-gray-500">
                              {requests.length === 0
                                ? "Tenants haven't submitted any maintenance requests yet."
                                : "Try adjusting your search or filters."}
                            </div>
                          </div>
                        ) : (
                          <div className="d-flex flex-column gap-3">
                            {filtered.map((req) => {
                              const s = (req.status || "").toLowerCase();
                              let barClass = "bg-warning";
                              let hoverBorderClass = "border-hover-warning";
                              let statusLabel = "In Progress";
                              let statusColor = "#D97706";

                              if (s.includes("complete") || s.includes("resolve")) {
                                barClass = "bg-success";
                                hoverBorderClass = "border-hover-success";
                                statusLabel = "Completed";
                                statusColor = "#16A34A";
                              } else if (s.includes("cancel") || s.includes("reject")) {
                                barClass = "bg-danger";
                                hoverBorderClass = "border-hover-danger";
                                statusLabel = "Canceled";
                                statusColor = "#DC2626";
                              }

                              let bDisplay = req.buildingNumber || req.houseNumber || req.house || "";
                              if (bDisplay && !bDisplay.includes(" - ") && !bDisplay.toLowerCase().startsWith("phase")) {
                                bDisplay = req.phaseName && req.phaseName !== "—" ? `${req.phaseName} - ${bDisplay}` : bDisplay;
                              }

                              const isCompleted = s.includes("complete") || s.includes("resolve");
                              const priorityMeta = PRIORITY_META[req.priority] || { color: "text-gray-700" };
                              const firstFileUrl = req.uploadedFileUrls?.[0];

                              return (
                                <div
                                  key={req.id}
                                  className={`d6-card d6-list-card d-flex align-items-center mb-2 py-4 px-5 cursor-pointer transition-all ${hoverBorderClass} position-relative overflow-hidden`}
                                  style={{ minHeight: 90 }}
                                  onClick={() => handleOpenRequest(req)}
                                >
                                  <div className={`position-absolute top-0 start-0 h-100 ${barClass} w-4px`}></div>

                                  <div className="d-flex align-items-center me-4" style={{ width: "400px", flexShrink: 0 }}>
                                    <Avatar src={req.tenantProfilePictureUrl} name={req.tenantName} size={48} />
                                    <div className="d-flex flex-column overflow-hidden min-w-0 ms-3">
                                      <div className="d-flex align-items-center gap-2 mb-0.5 flex-wrap">
                                        <span className="fs-6 fw-bold text-gray-900 text-truncate">{req.tenantName}</span>
                                        {req.category && (
                                          <span className="badge bg-light-success text-success fs-9 fw-semibold rounded-pill px-2.5 py-0.5">{req.category}</span>
                                        )}
                                      </div>
                                      <div className="text-gray-500 fs-8 text-truncate">{bDisplay || "Unassigned"}</div>
                                    </div>
                                  </div>

                                  <div className="d-flex align-items-center justify-content-start flex-grow-1 mx-4 overflow-hidden">
                                    <div className="d-flex align-items-center gap-3 text-start overflow-hidden">
                                      <div className="rounded-3 flex-shrink-0 overflow-hidden border border-gray-200" style={{ width: 44, height: 44 }}>
                                        <img
                                          src={firstFileUrl || req.buildingImageUrl || getHouseImage(req.typeName || req.buildingNumber || req.phaseName)}
                                          alt="Request attachment or building"
                                          style={{ width: 44, height: 44, objectFit: "cover" }}
                                          onError={(e) => { e.target.src = "/assets/media/zangon/3bedroomsbungalo.jpg"; }}
                                        />
                                      </div>
                                      <div className="d-flex flex-column text-start overflow-hidden">
                                        <div className="text-gray-400 fs-9">Request title</div>
                                        <div className="fw-bold text-gray-900 text-truncate fs-7" title={req.title}>{req.title}</div>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="d-flex align-items-center gap-5 text-end ms-auto flex-shrink-0">
                                    <div className="d-none d-sm-block text-center">
                                      <div className="text-gray-400 fs-9 mb-0.5">Priority</div>
                                      <div className={`fw-bold fs-8 ${priorityMeta.color}`}>{req.priority}</div>
                                    </div>
                                    <div className="d-none d-sm-block text-center">
                                      <div className="text-gray-400 fs-9 mb-0.5">Created</div>
                                      <div className="fw-semibold text-gray-900 fs-8">{fmtDate(req.createdDate)}</div>
                                    </div>
                                    {isCompleted && (
                                      <div className="d-none d-sm-block text-center">
                                        <div className="text-gray-400 fs-9 mb-0.5">Completed</div>
                                        <div className="fw-semibold text-gray-900 fs-8">{fmtDate(req.lastModified)}</div>
                                      </div>
                                    )}
                                    <StatusChip color={statusColor}>{statusLabel}</StatusChip>
                                    <span className="d6-icon-btn" title="View request"><i className="ki-outline ki-arrow-up-right fs-5"></i></span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {!isLoading && (
                          <Pagination
                            page={page}
                            totalPages={totalPages}
                            onChange={setPage}
                            summary={`Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, totalCount)} of ${totalCount} requests`}
                          />
                        )}
                      </div>
                    </div>

                  </div>
                </div>
              </div>

              <div id="kt_app_footer" className="app-footer">
                  <div className="app-container container-fluid d-flex justify-content-center align-items-center py-3">
                    <div className="text-gray-900 text-center">
                      <span className="text-muted fw-semibold me-1">2026&copy;</span>
                      <a href="https://pixels.com.ng/" target="_blank" rel="noreferrer" className="text-gray-800 text-hover-primary">Powered by Pixels Solutions Limited</a>
                    </div>
                  </div>
                </div>
            </div>
          </div>

          <div id="kt_scrolltop" className="scrolltop bg-success" data-kt-scrolltop="true">
            <i className="ki-outline ki-arrow-up"></i>
          </div>
        </div>
      </div>
      <ManageCategoriesModal modalRef={categoriesModalRef} estateId={estateId} />
      <RequestDetailModal
        modalRef={detailModalRef}
        estateId={estateId}
        request={selectedRequest}
        onClose={() => setSelectedRequest(null)}
        onStatusChange={handleStatusChange}
      />
      <DownloadRequestsModal
        modalRef={downloadModalRef}
        estateId={estateId}
        filters={{ search, statusFilter, priorityFilter, phaseFilter, categoryFilter }}
      />
    </>
  );
}